import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useRmaQuoteContext, useCreateRmaFromQuote } from '../../hooks/useRma';
import { generateRmaLabel, getRmaLabelUrl } from '../../api';
import { useSites } from '@/features/sites/hooks/useSites';
import type { Site } from '@/features/sites/types';
import {
  CP_SERVICE_LABELS,
  RMA_REASON_LABELS,
  type CpServiceCode,
  type RmaReason,
  type RmaMacOrigin,
  type RmaOrder,
  type CreateRmaFromQuoteInput,
} from '../../types';

type RmaSource = 'shipping' | 'demo';

interface EditableItem {
  key: string;
  kind: 'transmitter' | 'material';
  sku: string;
  productName: string;
  quantity: number;
  serialNumber: string; // MAC / dev_id — editable, may be empty
  macOrigin?: RmaMacOrigin;
  checked: boolean;
}

interface CreateRmaFromQuoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quoteId?: string | null;
  /** 'shipping' seeds MACs from the OLD transmitters (sensors box); 'demo' from the shipped devices. */
  source: RmaSource;
  /** Called after a successful create (and optional label). */
  onCreated?: (rma: RmaOrder) => void;
}

const REASON_CODES: RmaReason[] = ['defective', 'damaged', 'wrong_item', 'calibration', 'end_of_life', 'other'];
const SERVICE_CODES = Object.keys(CP_SERVICE_LABELS) as CpServiceCode[];

export function CreateRmaFromQuoteModal({
  open,
  onOpenChange,
  quoteId,
  source,
  onCreated,
}: CreateRmaFromQuoteModalProps) {
  const { t } = useTranslation('operations');
  const navigate = useNavigate();

  const { data: context, isLoading, isError } = useRmaQuoteContext(quoteId, open);
  const createFromQuote = useCreateRmaFromQuote();
  // Resolved to a string so the seed effect below depends on it BY VALUE — a new
  // `t` identity per render must not re-seed and wipe the operator's edits.
  const defaultTransmitterName = t('rma.fromQuote.defaultTransmitterName', 'Transmitter');

  const [items, setItems] = useState<EditableItem[]>([]);
  const [reason, setReason] = useState<RmaReason>('other');
  const [serviceCode, setServiceCode] = useState<CpServiceCode>('DOM.RP');
  const [shipFrom, setShipFrom] = useState({
    name: '', company: '', line1: '', line2: '', city: '', province: '', postalCode: '',
  });
  // Ship-from site picker: the client account's saved sites (from PG), default-
  // selected to the quote's shipping site. Selecting fills the address fields;
  // they stay editable. The default comes from the quote-context defaultSiteId.
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(undefined);
  const { data: sitesData } = useSites(
    { accountId: context?.accountId ?? undefined },
    { enabled: open && !!context?.accountId },
  );
  const accountSites = useMemo(() => sitesData?.sites ?? [], [sitesData]);
  const siteOptionValue = (s: Site) => s.mongoSiteId ?? s.id;
  const handleShipFromSiteSelect = (value: string) => {
    setSelectedSiteId(value);
    const site = accountSites.find((s) => siteOptionValue(s) === value);
    if (!site) return;
    setShipFrom({
      name: site.name ?? '',
      company: '',
      line1: site.addressLine1 ?? '',
      line2: site.addressLine2 ?? '',
      city: site.city ?? '',
      province: site.stateProvince ?? '',
      postalCode: site.postalCode ?? '',
    });
  };
  const [submitting, setSubmitting] = useState(false);
  const [createdRma, setCreatedRma] = useState<RmaOrder | null>(null);
  const [labelGenerated, setLabelGenerated] = useState(false);

  // Seed the editable preview once the context arrives. Guard on `open` so a
  // closed modal never seeds (otherwise it can reopen blank on the next mount).
  useEffect(() => {
    if (!open || !context) return;
    const transmitterRows: EditableItem[] = (
      source === 'shipping'
        ? context.resolvedTransmitters.map((tx, i) => ({
            key: `tx-${tx.devId}-${i}`,
            kind: 'transmitter' as const,
            sku: '',
            productName: tx.transmitterName || defaultTransmitterName,
            quantity: 1,
            serialNumber: tx.devId,
            macOrigin: 'sensors_chain' as RmaMacOrigin,
            checked: true,
          }))
        : context.shippedDevices.map((d, i) => ({
            key: `dev-${d.devId}-${i}`,
            kind: 'transmitter' as const,
            sku: '',
            productName: d.deviceType || defaultTransmitterName,
            quantity: 1,
            serialNumber: d.devId,
            macOrigin: 'quote_device_links' as RmaMacOrigin,
            checked: true,
          }))
    );
    const materialRows: EditableItem[] = context.materialLines.map((m, i) => ({
      key: `mat-${m.sku}-${i}`,
      kind: 'material',
      sku: m.sku,
      productName: m.productName || m.sku,
      quantity: m.quantity,
      serialNumber: '',
      checked: true,
    }));
    setItems([...transmitterRows, ...materialRows]);
    setReason('other');
    setServiceCode('DOM.RP');
    setShipFrom({
      name: context.shipFrom.name || '',
      company: context.shipFrom.company || '',
      line1: context.shipFrom.line1 || '',
      line2: context.shipFrom.line2 || '',
      city: context.shipFrom.city || '',
      province: context.shipFrom.province || '',
      postalCode: context.shipFrom.postalCode || '',
    });
    setSelectedSiteId(context.defaultSiteId ?? undefined);
    setCreatedRma(null);
    setLabelGenerated(false);
  }, [open, context, source, defaultTransmitterName]);

  const checkedCount = useMemo(() => items.filter((i) => i.checked).length, [items]);
  const unresolvedSensors = context
    ? context.sensorCount > 0 && context.distinctTransmitterCount === 0
    : false;

  function patchItem(key: string, patch: Partial<EditableItem>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }

  function buildPayload(): CreateRmaFromQuoteInput | null {
    if (!context?.accountId) return null;
    return {
      accountId: context.accountId,
      quoteId: context.quoteId,
      quoteNumber: context.quoteNumber || undefined,
      source,
      reason,
      cpServiceCode: serviceCode,
      shipFromName: shipFrom.name || null,
      shipFromCompany: shipFrom.company || null,
      shipFromLine1: shipFrom.line1 || null,
      shipFromLine2: shipFrom.line2 || null,
      shipFromCity: shipFrom.city || null,
      shipFromProvince: shipFrom.province || null,
      shipFromPostalCode: shipFrom.postalCode || null,
      items: items
        .filter((i) => i.checked)
        .map((i) => ({
          sku: i.sku,
          productName: i.productName || null,
          quantity: i.quantity,
          serialNumber: i.serialNumber || null,
          reason,
          macOrigin: i.macOrigin,
        })),
    };
  }

  async function handleCreate(withLabel: boolean) {
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    try {
      const rma = await createFromQuote.mutateAsync(payload);
      setCreatedRma(rma);
      if (withLabel) {
        try {
          await generateRmaLabel(rma.id, {});
          setLabelGenerated(true);
          toast.success(t('rma.fromQuote.labelGenerated', 'Return label generated'));
        } catch (labelErr) {
          // Best-effort: a label failure must NEVER undo the created RMA (or the
          // already-confirmed outbound shipment). Surface it, keep the RMA.
          const description = labelErr instanceof Error ? labelErr.message : String(labelErr);
          toast.error(
            t('rma.fromQuote.labelFailed', 'RMA created, but the return label could not be generated'),
            { description },
          );
        }
      } else {
        toast.success(t('rma.fromQuote.created', 'RMA created'));
      }
      onCreated?.(rma);
    } catch (err) {
      const description = err instanceof Error ? err.message : String(err);
      toast.error(t('rma.fromQuote.createFailed', 'Failed to create RMA'), { description });
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !!context?.accountId && !submitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('rma.fromQuote.title', 'Create RMA from quote')}</DialogTitle>
          <DialogDescription>
            {source === 'shipping'
              ? t('rma.fromQuote.subtitleShipping', 'Prepaid return label for the device coming back to ATEK.')
              : t('rma.fromQuote.subtitleDemo', 'Recover the devices sent for this quote.')}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {t('rma.fromQuote.loading', 'Loading quote details…')}
          </div>
        )}

        {isError && (
          <div className="py-6 text-sm text-destructive">
            {t('rma.fromQuote.loadError', 'Could not load the quote details.')}
          </div>
        )}

        {!isLoading && !isError && context && !createdRma && (
          <div className="space-y-5">
            {context.existingRmaCount > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                {t('rma.fromQuote.existingRmas', 'This quote already has {{count}} RMA(s).', {
                  count: context.existingRmaCount,
                })}
              </div>
            )}
            {unresolvedSensors && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {t(
                    'rma.fromQuote.noTransmitterResolved',
                    'No transmitter could be resolved from the {{count}} sensor(s) on this quote — enter the MAC manually if needed.',
                    { count: context.sensorCount },
                  )}
                </span>
              </div>
            )}

            {/* Items */}
            <div className="space-y-2">
              <Label>{t('rma.fromQuote.itemsHeading', 'Items to return')}</Label>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead>{t('rma.fromQuote.colItem', 'Item')}</TableHead>
                      <TableHead className="w-24">{t('rma.fromQuote.colQty', 'Qty')}</TableHead>
                      <TableHead className="w-52">{t('rma.fromQuote.colMac', 'MAC (returning)')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-6">
                          {t('rma.fromQuote.noItems', 'No material items found on this quote.')}
                        </TableCell>
                      </TableRow>
                    )}
                    {items.map((item) => (
                      <TableRow key={item.key} data-testid="rma-from-quote-item">
                        <TableCell>
                          <Checkbox
                            checked={item.checked}
                            onCheckedChange={(v) => patchItem(item.key, { checked: v === true })}
                            aria-label={t('rma.fromQuote.includeItem', 'Include item')}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{item.productName}</div>
                          {item.sku && <div className="text-xs text-muted-foreground">{item.sku}</div>}
                        </TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          <Input
                            value={item.serialNumber}
                            onChange={(e) => patchItem(item.key, { serialNumber: e.target.value })}
                            placeholder={t('rma.fromQuote.macPlaceholder', 'optional')}
                            className="h-8"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('rma.fromQuote.itemsHint', '{{count}} item(s) selected.', { count: checkedCount })}
              </p>
            </div>

            {/* Reason + service code */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('rma.fromQuote.reason', 'Reason')}</Label>
                <Select value={reason} onValueChange={(v) => setReason(v as RmaReason)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASON_CODES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {t(`rma.reason.${code}`, RMA_REASON_LABELS[code])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('rma.fromQuote.serviceCode', 'Shipping service')}</Label>
                <Select value={serviceCode} onValueChange={(v) => setServiceCode(v as CpServiceCode)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_CODES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {t(`rma.service.${code}`, CP_SERVICE_LABELS[code])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ship-from (customer return address) */}
            <div className="space-y-2">
              <Label>{t('rma.fromQuote.shipFrom', 'Customer return address (From)')}</Label>
              {accountSites.length > 0 && (
                <Select value={selectedSiteId} onValueChange={handleShipFromSiteSelect}>
                  <SelectTrigger aria-label={t('rma.fromQuote.shipFromSite', 'Return address (from client sites)')}>
                    <SelectValue placeholder={t('rma.fromQuote.shipFromSite', 'Return address (from client sites)')} />
                  </SelectTrigger>
                  <SelectContent>
                    {accountSites.map((s) => (
                      <SelectItem key={siteOptionValue(s)} value={siteOptionValue(s)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Input value={shipFrom.name} onChange={(e) => setShipFrom((s) => ({ ...s, name: e.target.value }))} placeholder={t('rma.fromQuote.name', 'Name')} />
                <Input value={shipFrom.company} onChange={(e) => setShipFrom((s) => ({ ...s, company: e.target.value }))} placeholder={t('rma.fromQuote.company', 'Company')} />
                <Input value={shipFrom.line1} onChange={(e) => setShipFrom((s) => ({ ...s, line1: e.target.value }))} placeholder={t('rma.fromQuote.line1', 'Address line 1')} />
                <Input value={shipFrom.line2} onChange={(e) => setShipFrom((s) => ({ ...s, line2: e.target.value }))} placeholder={t('rma.fromQuote.line2', 'Address line 2')} />
                <Input value={shipFrom.city} onChange={(e) => setShipFrom((s) => ({ ...s, city: e.target.value }))} placeholder={t('rma.fromQuote.city', 'City')} />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={shipFrom.province} onChange={(e) => setShipFrom((s) => ({ ...s, province: e.target.value }))} placeholder={t('rma.fromQuote.province', 'Prov')} maxLength={2} />
                  <Input value={shipFrom.postalCode} onChange={(e) => setShipFrom((s) => ({ ...s, postalCode: e.target.value }))} placeholder={t('rma.fromQuote.postalCode', 'Postal')} maxLength={7} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Created (done) state */}
        {createdRma && (
          <div className="space-y-4 py-2">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
              {t('rma.fromQuote.createdWithNumber', 'RMA {{number}} created.', { number: createdRma.rmaNumber })}
            </div>
            {labelGenerated && (
              <a
                href={getRmaLabelUrl(createdRma.id)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary underline"
              >
                <Download className="h-4 w-4" />
                {t('rma.fromQuote.downloadLabel', 'Download return label (PDF)')}
              </a>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {!createdRma ? (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                {t('rma.fromQuote.cancel', 'Cancel')}
              </Button>
              <Button type="button" variant="outline" onClick={() => handleCreate(false)} disabled={!canSubmit}>
                {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {t('rma.fromQuote.createDraft', 'Create draft')}
              </Button>
              <Button type="button" onClick={() => handleCreate(true)} disabled={!canSubmit}>
                {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {t('rma.fromQuote.createAndLabel', 'Create & generate label')}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/operations/rma/${createdRma.id}`)}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                {t('rma.fromQuote.viewRma', 'View RMA')}
              </Button>
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t('rma.fromQuote.done', 'Done')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
